/**
 * IMPORTS
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  INTERVALO_ATUALIZACAO_MS,
  NOTIFICACOES_QUERY_KEY,
} from "@/hooks/conversa-cliente/use-notificacoes";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import {
  handleBuscarEstadoConversa,
  handleConvidarParticipante,
  handleInativarParticipante,
  handleListarCandidatosConvite,
  handleReativarParticipante,
  handleRemoverParticipante,
  handleResponderConvite,
} from "@/domain/use-cases/conversa-cliente";
import type { IConvidarParticipantePayload } from "@/interface/conversa-cliente";

import { CONVERSA_QUERY_KEY } from "./use-minhas-mensagens";

const PARTICIPANTES_QUERY_KEY = "conversa-cliente-participantes";

/** Se a conversa foi iniciada, se o usuário entra nela e quem participa. */
function useEstadoConversa() {
  const accessToken = useStoreZustandUserAuth((state) => state.token);

  return useQuery({
    queryKey: [PARTICIPANTES_QUERY_KEY, "estado"],
    queryFn: () => handleBuscarEstadoConversa(accessToken!),
    enabled: Boolean(accessToken),
    refetchInterval: INTERVALO_ATUALIZACAO_MS,
  });
}

/** Pessoas da empresa que o dono pode convidar (só consulta com o painel aberto). */
function useCandidatosConvite(busca: string, habilitado: boolean) {
  const accessToken = useStoreZustandUserAuth((state) => state.token);

  return useQuery({
    queryKey: [PARTICIPANTES_QUERY_KEY, "candidatos", busca],
    queryFn: () => handleListarCandidatosConvite(accessToken!, busca || undefined),
    enabled: Boolean(accessToken && habilitado),
  });
}

function useGerenciarParticipantes() {
  const accessToken = useStoreZustandUserAuth((state) => state.token);
  const queryClient = useQueryClient();
  const invalidar = () => queryClient.invalidateQueries({ queryKey: [PARTICIPANTES_QUERY_KEY] });

  const convidar = useMutation({
    mutationFn: (payload: IConvidarParticipantePayload) =>
      handleConvidarParticipante(accessToken!, payload),
    onSuccess: invalidar,
  });
  const remover = useMutation({
    mutationFn: (participanteId: number) => handleRemoverParticipante(accessToken!, participanteId),
    onSuccess: invalidar,
  });
  // Recarrega também no erro: a regra pode ter mudado no servidor (ex.: já inativado por outro dono).
  const inativar = useMutation({
    mutationFn: (participanteId: number) =>
      handleInativarParticipante(accessToken!, participanteId),
    onSettled: invalidar,
  });
  const reativar = useMutation({
    mutationFn: (participanteId: number) =>
      handleReativarParticipante(accessToken!, participanteId),
    onSettled: invalidar,
  });

  return { convidar, remover, inativar, reativar };
}

/** Aceitar/recusar o convite: atualiza notificações, estado e mensagens da conversa. */
function useResponderConvite() {
  const accessToken = useStoreZustandUserAuth((state) => state.token);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conviteId, resposta }: { conviteId: number; resposta: "aceitar" | "recusar" }) =>
      handleResponderConvite(accessToken!, conviteId, resposta),
    onSettled: () =>
      Promise.all(
        [NOTIFICACOES_QUERY_KEY, PARTICIPANTES_QUERY_KEY, CONVERSA_QUERY_KEY].map((chave) =>
          queryClient.invalidateQueries({ queryKey: [chave] })
        )
      ),
  });
}

/**
 * EXPORTS
 */
export { useCandidatosConvite, useEstadoConversa, useGerenciarParticipantes, useResponderConvite };
